import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import {
  IPartnerConfigurationApplyResponse,
  IPartnerConfigurationValues,
  PARTNER_CONFIGURATION_ALL,
  PARTNER_CONFIGURATION_COUNTRIES,
} from "../../interfaces";
import { AlertTriangle } from "react-feather";
import { countryName, FIELDS, formatValue, PendingChange } from "./configuration-model";
import { confirmWhitelistEdit, FieldInput } from "./field-input";

const changesWhitelist = (c: PendingChange) =>
  c.change.op === "update" && c.before?.currentWhitelistDomainsVersion !== c.after?.currentWhitelistDomainsVersion;

type FieldKey = keyof IPartnerConfigurationValues;

export const OP_BADGE: Record<string, { bg: string; label: string }> = {
  create: { bg: "success", label: "Add" },
  update: { bg: "primary", label: "Change" },
  delete: { bg: "danger", label: "Remove" },
};

export const ChangeSummary: React.FC<{ before: IPartnerConfigurationValues | null; after: IPartnerConfigurationValues | null; rowKey: string }> = ({
  before,
  after,
  rowKey,
}) => {
  if (!after) {
    return (
      <span className="small text-muted">
        {rowKey === PARTNER_CONFIGURATION_ALL
          ? "Countries without their own row fall back to the next ALL row, or Platform defaults."
          : "This country falls back to the ALL row, or Platform defaults if there is none."}
      </span>
    );
  }
  const fields = before ? FIELDS.filter((f) => before[f.key] !== after[f.key]) : FIELDS;
  return (
    <ul className="list-unstyled small mb-0">
      {fields.map((f) => (
        <li key={f.key} className={before && f.key === "currentWhitelistDomainsVersion" ? "text-danger" : undefined}>
          {before && f.key === "currentWhitelistDomainsVersion" && <AlertTriangle size={12} className="me-1" />}
          <span className="text-muted">{f.label}:</span>{" "}
          {before && (
            <>
              <span className="text-decoration-line-through text-muted">{formatValue(f.key, before[f.key])}</span> →{" "}
            </>
          )}
          <strong>{formatValue(f.key, after[f.key])}</strong>
        </li>
      ))}
    </ul>
  );
};

const ValuesForm: React.FC<{
  values: IPartnerConfigurationValues;
  onChange: (v: IPartnerConfigurationValues) => void;
  enabled?: Set<FieldKey>;
  onToggle?: (field: FieldKey) => void;
}> = ({ values, onChange, enabled, onToggle }) => (
  <Row className="g-3">
    {FIELDS.map((f) => (
      <Col md={4} key={f.key}>
        <Form.Label className="small fw-semibold mb-1 d-flex align-items-center gap-2">
          {onToggle && <Form.Check type="checkbox" checked={enabled?.has(f.key) ?? false} onChange={() => onToggle(f.key)} aria-label={`Change ${f.label}`} />}
          {f.label}
        </Form.Label>
        <FieldInput field={f.key} values={values} onChange={onChange} disabled={!!onToggle && !enabled?.has(f.key)} />
        <Form.Text className="text-muted d-block" style={{ fontSize: "0.72rem" }}>
          {f.hint}
        </Form.Text>
      </Col>
    ))}
  </Row>
);

export const AddCountriesModal: React.FC<{
  partnerLabel: string;
  existing: Set<string>;
  template: { values: IPartnerConfigurationValues; source: string };
  onAdd: (rowKeys: string[], values: IPartnerConfigurationValues) => void;
  onHide: () => void;
}> = ({ partnerLabel, existing, template, onAdd, onHide }) => {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [values, setValues] = useState(template.values);

  const available = useMemo(
    () => [PARTNER_CONFIGURATION_ALL, ...PARTNER_CONFIGURATION_COUNTRIES].filter((c) => !existing.has(c)),
    [existing]
  );
  const visible = available.filter((c) => {
    const q = search.trim().toLowerCase();
    return !q || c.toLowerCase().includes(q) || countryName(c).toLowerCase().includes(q);
  });
  const toggle = (c: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  return (
    <Modal show onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">Add countries for {partnerLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Pick the countries that need their own settings. <strong>ALL</strong> covers every country without its own row. Nothing is saved until you
          review and confirm.
        </p>
        <div className="d-flex gap-2 mb-2">
          <Form.Control size="sm" placeholder="Search country or code" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
          <Button size="sm" variant="outline-secondary" onClick={() => setPicked(new Set(Array.from(picked).concat(visible)))} title="Tick every country in the list below">
            Select shown ({visible.length})
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setPicked(new Set())} disabled={!picked.size}>
            Clear
          </Button>
        </div>
        <div className="border rounded p-2 mb-3" style={{ maxHeight: 220, overflowY: "auto" }}>
          <Row className="g-1">
            {visible.map((c) => (
              <Col xs={6} md={3} key={c}>
                <Form.Check
                  type="checkbox"
                  id={`add-${c}`}
                  checked={picked.has(c)}
                  onChange={() => toggle(c)}
                  label={
                    <span className="small">
                      <strong>{c}</strong> {countryName(c)}
                    </span>
                  }
                />
              </Col>
            ))}
            {!visible.length && <div className="small text-muted p-2">Every matching country already has a row.</div>}
          </Row>
        </div>
        <h6 className="mb-1">Settings for the new rows</h6>
        <p className="small text-muted mb-2">Prefilled from {template.source}. You can change individual countries afterwards.</p>
        <ValuesForm values={values} onChange={setValues} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!picked.size} onClick={() => onAdd(Array.from(picked), values)}>
          Add {picked.size || ""} {picked.size === 1 ? "row" : "rows"} to draft
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const BulkEditModal: React.FC<{
  count: number;
  initial: IPartnerConfigurationValues;
  onApply: (fields: FieldKey[], values: IPartnerConfigurationValues) => void;
  onHide: () => void;
}> = ({ count, initial, onApply, onHide }) => {
  const [values, setValues] = useState(initial);
  const [enabled, setEnabled] = useState<Set<FieldKey>>(new Set());
  const toggle = (f: FieldKey) => {
    if (f === "currentWhitelistDomainsVersion" && !enabled.has(f) && !confirmWhitelistEdit()) return;
    setEnabled((s) => {
      const next = new Set(s);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  };

  return (
    <Modal show onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          Edit {count} selected {count === 1 ? "row" : "rows"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">Tick the settings to overwrite. Unticked settings keep each row's own value.</p>
        <ValuesForm values={values} onChange={setValues} enabled={enabled} onToggle={toggle} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!enabled.size} onClick={() => onApply(Array.from(enabled), values)}>
          Apply to {count} {count === 1 ? "row" : "rows"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const ReviewChangesModal: React.FC<{
  changes: PendingChange[];
  partnerLabel: (partitionKey: string) => string;
  saving: boolean;
  result: IPartnerConfigurationApplyResponse | null;
  error: string | null;
  onSave: () => void;
  onHide: () => void;
}> = ({ changes, partnerLabel, saving, result, error, onSave, onHide }) => {
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const allConfirmed = changes.length > 0 && changes.every((c) => confirmed.has(c.key));
  const toggle = (key: string) =>
    setConfirmed((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const counts = changes.reduce<Record<string, number>>((acc, c) => ({ ...acc, [c.change.op]: (acc[c.change.op] ?? 0) + 1 }), {});
  const failures = result?.results.filter((r) => !r.ok) ?? [];
  const whitelistChanges = changes.filter(changesWhitelist).length;
  const staleChanges = changes.filter((c) => c.stale).length;

  return (
    <Modal show onHide={saving ? undefined : onHide} size="xl" backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title className="h5">Review and confirm changes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <>
            <Alert variant={result.failed ? "warning" : "success"}>
              {result.applied} {result.applied === 1 ? "change" : "changes"} saved
              {result.failed ? `, ${result.failed} not saved. They stay in your draft; refresh to see what changed and try again.` : "."} The Platform API picks
              them up when its configuration cache expires.
            </Alert>
            {failures.length > 0 && (
              <Table size="sm" className="small">
                <tbody>
                  {failures.map((f) => (
                    <tr key={`${f.partitionKey}|${f.rowKey}`}>
                      <td>{partnerLabel(f.partitionKey)}</td>
                      <td>{f.rowKey}</td>
                      <td>{OP_BADGE[f.op].label}</td>
                      <td className="text-danger">{f.error}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <>
            <p className="small text-muted">
              These writes go straight to the live partner configuration table. Tick each one to confirm it; Save unlocks once every change is confirmed.
            </p>
            <div className="d-flex gap-2 mb-2">
              {Object.entries(counts).map(([op, n]) => (
                <Badge key={op} bg={OP_BADGE[op].bg}>
                  {OP_BADGE[op].label}: {n}
                </Badge>
              ))}
            </div>
            {whitelistChanges > 0 && (
              <Alert variant="warning" className="small">
                <AlertTriangle size={14} className="me-1" />
                {whitelistChanges} {whitelistChanges === 1 ? "change moves" : "changes move"} the whitelist version, which is normally generated
                automatically. Clients will download that version's whitelisted-domains list, so make sure it exists for each country.
              </Alert>
            )}
            {staleChanges > 0 && (
              <Alert variant="danger" className="small">
                {staleChanges} {staleChanges === 1 ? "row was" : "rows were"} changed by someone else after you started editing. Go back, check the current
                values, then choose <strong>Keep mine</strong> or <strong>Revert</strong> on each one before saving.
              </Alert>
            )}
            {error && <Alert variant="danger">{error}</Alert>}
            <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <Table size="sm" hover className="align-middle">
                <thead className="sticky-top bg-body">
                  <tr>
                    <th style={{ width: 40 }}>
                      <Form.Check
                        type="checkbox"
                        checked={allConfirmed}
                        onChange={() => setConfirmed(allConfirmed ? new Set() : new Set(changes.map((c) => c.key)))}
                        title="Confirm every change listed"
                      />
                    </th>
                    <th>Partner</th>
                    <th>Country</th>
                    <th>Action</th>
                    <th>What changes</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.key} className={c.stale ? "table-danger" : confirmed.has(c.key) ? "" : "table-warning"} onClick={() => toggle(c.key)} style={{ cursor: "pointer" }}>
                      <td>
                        <Form.Check type="checkbox" checked={confirmed.has(c.key)} onChange={() => toggle(c.key)} onClick={(e) => e.stopPropagation()} />
                      </td>
                      <td className="small">{partnerLabel(c.change.partitionKey)}</td>
                      <td className="small">
                        <strong>{c.change.rowKey}</strong> <span className="text-muted">{countryName(c.change.rowKey)}</span>
                      </td>
                      <td>
                        <Badge bg={OP_BADGE[c.change.op].bg}>{OP_BADGE[c.change.op].label}</Badge>
                      </td>
                      <td>
                        <ChangeSummary before={c.before} after={c.after} rowKey={c.change.rowKey} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button onClick={onHide}>Close</Button>
        ) : (
          <>
            <span className="small text-muted me-auto">
              {confirmed.size} of {changes.length} confirmed
            </span>
            <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
              Back to editing
            </Button>
            <Button onClick={onSave} disabled={!allConfirmed || saving || staleChanges > 0}>
              {saving ? <Spinner animation="border" size="sm" /> : `Save ${changes.length} ${changes.length === 1 ? "change" : "changes"}`}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
